/**
 * Meridian building blocks for the Image Quality page (hybrid variant). Every colour comes from
 * the active Meridian theme (useTheme), so the page follows RCC's light/dark mode.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Minibar from '@amzn/meridian/minibar';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import useTheme from '@amzn/meridian/theme/use-theme';
import { formatTimestamp } from '../flowPlanning/dashboard/utils';
import { Cohort, CohortFilter, ListCohort, QualityTier } from './constants';
import { AssociateImageQuality, QualityThresholds } from './model';
import { associateCohort, cohortLabel, qualityTier } from './imageQualityUtils';
import { IqCrumb } from './useImageQualityFilters';

/** Theme-aware colours for tiers, bars, failure modes and the focus accent. */
export const useIqColors = () => {
  // useTheme() returns a lookup function over the active (light/dark) theme tokens.
  const themeToken = useTheme();
  const failure: Record<string, string> = {
    PII: themeToken('themeSupportBorderError'),
    BLUR: themeToken('themeDecorativeNeutralStrong'),
    DAMAGE: themeToken('themeDecorativeNeutralDefault'),
  };
  return {
    bar: {
      good: themeToken('themeSupportBorderSuccess'),
      warn: themeToken('themeSupportBorderWarning'),
      bad: themeToken('themeSupportBorderError'),
    } as Record<QualityTier, string>,
    track: themeToken('themeBorderSubdued'),
    accent: themeToken('themeActionPrimaryDefault'),
    /** Light fill + ink per tier, for small badges (TOP / MID / LOW). */
    badge: {
      good: {
        fill: themeToken('themeSupportFillSuccess'),
        ink: themeToken('themeSupportInkSuccess'),
      },
      warn: {
        fill: themeToken('themeSupportFillWarning'),
        ink: themeToken('themeSupportInkWarning'),
      },
      bad: { fill: themeToken('themeSupportFillError'), ink: themeToken('themeSupportInkError') },
    } as Record<QualityTier, { fill: string; ink: string }>,
    /** Pill toggles: off = default border + subdued ink; on = primary action colour. */
    pill: {
      border: themeToken('themeBorderDefault'),
      ink: themeToken('themeInkSubdued'),
      surface: themeToken('themeSurfaceDefault'),
    },
    /** Failure-mode colour; unknown checks fall back to neutral and still render. */
    failure: (checkId: string): string =>
      failure[checkId] ?? themeToken('themeDecorativeNeutralDefault'),
  };
};

/** Tier → Meridian semantic text colour. */
export const tierToTextColor: Record<QualityTier, 'success' | 'warning' | 'error'> = {
  good: 'success',
  warn: 'warning',
  bad: 'error',
};

/** Meridian text colour for a tier; the default colour when there is no tier (no photos). */
export const textColorForTier = (
  tier: QualityTier | null
): 'success' | 'warning' | 'error' | undefined => (tier ? tierToTextColor[tier] : undefined);

/** Small uppercase section label (the mock's "IMAGE QUALITY", "FAILURE BREAKDOWN"). */
export const Label: React.FC<{ children: string; count?: string }> = ({ children, count }) => (
  <Row spacing="300">
    <Text type="h100" color="secondary">
      {children.toUpperCase()}
    </Text>
    {count && (
      <Text type="b200" color="secondary">
        {count.toUpperCase()}
      </Text>
    )}
  </Row>
);

/** Headline tile: label, big number, sub-line. */
export const StatCard: React.FC<{
  label: string;
  hero: string;
  sub: string;
  heroColor?: 'success' | 'warning' | 'error';
  testId?: string;
}> = ({ label, hero, sub, heroColor, testId }) => (
  <Card spacingInset="500" data-testid={testId}>
    <Column spacing="300">
      <Label>{label}</Label>
      <Text type="h600" color={heroColor}>
        {hero}
      </Text>
      <Text type="b300" color="secondary">
        {sub}
      </Text>
    </Column>
  </Card>
);

/**
 * Card with a theme-accent bar down the left edge ("Summary — where to focus"), with a panel on
 * the right (the mock puts the failure breakdown there).
 */
export const AccentCard: React.FC<{
  testId?: string;
  aside: React.ReactNode;
  children: React.ReactNode;
}> = ({ testId, aside, children }) => {
  const { accent } = useIqColors();
  return (
    <Card spacingInset="none" data-testid={testId}>
      <Row spacing="none" alignmentVertical="stretch" widths={[4, 'fill', 340]}>
        <Box backgroundColor={accent} width={4} />
        <Box spacingInset="500">
          <Column spacing="400">
            <Label>Summary — where to focus</Label>
            {children}
          </Column>
        </Box>
        <Box spacingInset="500">{aside}</Box>
      </Row>
    </Card>
  );
};

/**
 * Rounded 0–100 bar (Meridian Minibar). Minibar draws a 12px inline <svg>; inline SVG sits on the
 * text baseline, which pushes it ~6px below its box. The last bar in a card then crosses the
 * card's overflow-clipping edge and gets cut in half. `line-height: 0` on a wrapper pins the SVG
 * to the top of a box sized to it.
 */
const MINIBAR_HEIGHT = 12;

export const QualityBar: React.FC<{
  pct: number;
  color: string;
  width?: number | string;
  label: string;
}> = ({ pct, color, width = 170, label }) => {
  const { track } = useIqColors();
  return (
    <div style={{ lineHeight: 0, width }}>
      <Minibar
        aria-label={label}
        direction="horizontal"
        capacity={100}
        data={[{ value: Math.max(0, Math.min(100, pct)), color }]}
        trackColor={track}
        width="100%"
        height={MINIBAR_HEIGHT}
      />
    </div>
  );
};

/** A pass rate as a bar in its tier's colour; an empty cell when there is no pass rate (no photos). */
export const PassRateBar: React.FC<{
  pct: number | null;
  thresholds: QualityThresholds;
  width?: number | string;
  label: string;
}> = ({ pct, thresholds, width, label }) => {
  const { bar } = useIqColors();
  if (pct === null) return <span />;
  return (
    <QualityBar pct={pct} color={bar[qualityTier(pct, thresholds)]} width={width} label={label} />
  );
};

/** Outlined chip with an optional status dot (not a Meridian Tag pill, per the mock). */
export const Chip: React.FC<{ dotColor?: string; muted?: boolean; children: string }> = ({
  dotColor,
  muted,
  children,
}) => (
  // Row keeps the chip at its content width inside table cells.
  <Row>
    <Box
      type={muted ? 'fill' : 'outline'}
      backgroundColor={muted ? 'subdued' : undefined}
      spacingInset="100 300"
    >
      <Row spacing="200" alignmentVertical="center">
        {dotColor && (
          <Text tag="span" type="b100">
            <span style={{ color: dotColor }} aria-hidden>
              ●
            </span>
          </Text>
        )}
        <Text type="b300">{children}</Text>
      </Row>
    </Box>
  </Row>
);

/** All sites › CHA1 › login. Earlier crumbs navigate; the last is the current view. */
// Meridian requires a handler on every crumb but renders the current one disabled, so this never runs.
/* istanbul ignore next */
const stayOnCurrentView = () => undefined;

export const Breadcrumbs: React.FC<{ crumbs: IqCrumb[] }> = ({ crumbs }) => (
  <div className="iq-no-print" data-testid="iq-breadcrumbs">
    <BreadcrumbGroup>
      {crumbs.map(crumb =>
        crumb.onClick ? (
          <Breadcrumb key={crumb.key} onClick={crumb.onClick} data-testid={`iq-crumb-${crumb.key}`}>
            {crumb.label}
          </Breadcrumb>
        ) : (
          <Breadcrumb
            key={crumb.key}
            onClick={stayOnCurrentView}
            aria-current="page"
            data-testid={`iq-crumb-${crumb.key}`}
          >
            {crumb.label}
          </Breadcrumb>
        )
      )}
    </BreadcrumbGroup>
  </div>
);

/**
 * Opens the browser print dialog (which also offers "Save as PDF"). Text-only tertiary button,
 * as in the mock — Meridian's print icon only ships at the large size and overpowers the label.
 */
/** A failed load, with a way to try the same request again. */
export const LoadFailed: React.FC<{ message: string; onRetry: () => void; testId: string }> = ({
  message,
  onRetry,
  testId,
}) => (
  <Row spacing="300" alignmentHorizontal="center" spacingInset="500" data-testid={testId}>
    <Text type="b300" color="error">
      {message}
    </Text>
    <Link onClick={onRetry}>Try again</Link>
  </Row>
);

export const PrintButton: React.FC = () => (
  <Button type="tertiary" onClick={() => window.print()} data-testid="iq-print">
    Print / PDF
  </Button>
);

/** "Data as of Oct 20, 09:14": when the newest photo in the window was captured. */
export const DataAsOf: React.FC<{ capturedAt?: number }> = ({ capturedAt }) =>
  capturedAt === undefined ? null : (
    <Box type="fill" backgroundColor="subdued" spacingInset="100 300" data-testid="iq-data-as-of">
      <Text type="b200" color="secondary">{`Data as of ${formatTimestamp(capturedAt)}`}</Text>
    </Box>
  );

export const PageTitle: React.FC<{
  title: string;
  sub: string;
  mono?: boolean;
  capturedAt?: number;
}> = ({ title, sub, mono, capturedAt }) => (
  <Column spacing="100">
    <Row spacing="400" alignmentVertical="center" wrap="down">
      <Heading level={2} type="h600">
        {mono ? (
          <Text tag="span" type="h600" fontFamily="amazonEmberMonospace">
            {title}
          </Text>
        ) : (
          title
        )}
      </Heading>
      <DataAsOf capturedAt={capturedAt} />
    </Row>
    <Text type="b400" color="secondary">
      {sub}
    </Text>
  </Column>
);

/**
 * One cohort filter pill: status dot, label and a count bubble; outlined when selected. A disabled
 * pill has no count and can't be selected.
 */
const CohortPill: React.FC<{
  label: string;
  count?: number;
  dotColor: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  testId: string;
}> = ({ label, count, dotColor, selected, disabled, onSelect, testId }) => {
  const { accent, pill, track } = useIqColors();
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      title={disabled ? 'Available once every associate is reported' : undefined}
      onClick={onSelect}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 14px',
        borderRadius: 999,
        border: `1.5px solid ${selected ? accent : 'transparent'}`,
        background: pill.surface,
        color: selected ? accent : 'inherit',
        fontSize: 15,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor }}
      />
      <span>{label}</span>
      {count !== undefined && (
        <span style={{ background: track, borderRadius: 999, padding: '0 8px', fontSize: 12 }}>
          {count}
        </span>
      )}
    </button>
  );
};

/** A list the report sent, shown as its own cohort pill (e.g. "Top 3", "Bottom 1%"). */
export interface ListCohortPill {
  value: ListCohort;
  label: string;
  count: number;
  tier: QualityTier;
}

/**
 * All / Top / Middle / Bottom pills, each with its count. With `listCohorts` (the report doesn't list
 * every associate yet), those pills come after All and the threshold pills are disabled.
 */
export const CohortChips: React.FC<{
  associates: AssociateImageQuality[];
  thresholds: QualityThresholds;
  listCohorts?: ListCohortPill[];
  value: CohortFilter;
  onChange: (value: CohortFilter) => void;
}> = ({ associates, thresholds, listCohorts, value, onChange }) => {
  const { bar, pill } = useIqColors();
  const countInCohort = (cohort: Cohort) =>
    associates.filter(associate => associateCohort(associate, thresholds) === cohort).length;
  const cohorts: { cohort: Cohort; tier: QualityTier }[] = [
    { cohort: Cohort.TOP, tier: 'good' },
    { cohort: Cohort.MIDDLE, tier: 'warn' },
    { cohort: Cohort.BOTTOM, tier: 'bad' },
  ];
  return (
    <Row spacing="300" alignmentVertical="center" wrap="down" data-testid="iq-cohorts">
      <Text type="h100" color="secondary">
        COHORT:
      </Text>
      <CohortPill
        label="All"
        count={associates.length}
        dotColor={pill.ink}
        selected={value === ''}
        onSelect={() => onChange('')}
        testId="iq-cohort-all"
      />
      {listCohorts?.map(listCohort => (
        <CohortPill
          key={listCohort.value}
          label={listCohort.label}
          count={listCohort.count}
          dotColor={bar[listCohort.tier]}
          selected={value === listCohort.value}
          onSelect={() => onChange(listCohort.value)}
          testId={`iq-cohort-${listCohort.value}`}
        />
      ))}
      {cohorts.map(({ cohort, tier }) => (
        <CohortPill
          key={cohort}
          label={cohortLabel(cohort, thresholds)}
          count={listCohorts ? undefined : countInCohort(cohort)}
          dotColor={bar[tier]}
          selected={value === cohort}
          disabled={!!listCohorts}
          onSelect={() => onChange(cohort)}
          testId={`iq-cohort-${cohort}`}
        />
      ))}
      <Text type="b200" color="secondary">
        Cohorts based on image quality
      </Text>
    </Row>
  );
};
